import { useEffect, useState } from "react";
import { Swiper } from "swiper/types";
import {
    ButtonShapes,
    ButtonTypes,
} from "../../stories/Components/Button/interface";
import {
    NavigationProps,
    NavButtonProps,
} from "../../stories/Components/Navigation/interface";
import {
    CardVariantsEnum,
    TransformedCardData,
} from "../../stories/Components/Card/interface";
import Card from "../../stories/Components/Card";
import { VIDEO_ASPECT_RATIOS } from "../VideoModal/constants";
import PlaylistCore from "../PlaylistCore";
import { trackSelectedButtonClick } from "../../actions/Tracking/utils";
import {
    ShortsNavConstantProps,
    SHORTS_WIDGET_NAVIGATION,
    SHORTS_FILTERS_LINKS_TRACKING_DATA_ATTRS,
    placeholderItem,
} from "./constants";
import { ShortsWidgetProps } from "./interface";
import { insertAdsAfterN } from "./insertAd";
import { personalizationABTest } from "./abtest";
import useABTest from "../../hooks/useABTest";

const PLACEHOLDER_CARDS = Array.from({ length: 5 }, (_, index) => (
    <Card
        item={placeholderItem}
        variant={CardVariantsEnum.MEDIA}
        key={`shorts-widget-${index}`}
    />
));

const ShortsWidget = ({ title, navigation }: ShortsWidgetProps) => {
    // navigation ojbect is mistakenly stored as string like "{"navigation":"homepage"}" in 9CMS
    // so we need to extract the value until we fix it in nine CMS
    const navigationValue =
        navigation?.split(":")[1].slice(1, -2) || "homepage";
    const [playingVideoIndex, setVideoPlayingIndex] = useState<number>(0);
    const [chosenNavIndex, setChosenNavIndex] = useState<number>(0);
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    /* eslint-disable */
    const [shortsData, setShortsData] = useState<TransformedCardData[] | []>(
        []
    );

    /** WEBX-3068-A/B Test For For You Filter on Vertical Video */
    const variant = useABTest("a-trseYm7fRT");

    const [widgetSwiperObject, setWidgetSwiperObject] = useState<Swiper | null>(
        null
    );
    /* eslint-enable */

    const navigationData: ShortsNavConstantProps[] =
        SHORTS_WIDGET_NAVIGATION[navigationValue];

    const onWidgetSwiper = (swiper: Swiper) => {
        setWidgetSwiperObject(swiper);
    };

    const onSlideChange = (swiper: Swiper) => {
        setVideoPlayingIndex(swiper.activeIndex); // eslint-disable-line
    };

    const handleClose = () => {
        setModalIsOpen(false);
    };

    const handleModalOpen = (index: number) => {
        return () => {
            setVideoPlayingIndex(index);
            setModalIsOpen(true);
        };
    };

    const handleNavigationClick = (index: number, label: string) => {
        const currentIndex = chosenNavIndex;
        if (currentIndex === index) return;

        // SELECTED verb should only be fired if the current category is not active
        // not if the user clicks it again (since the radio button is already selected)
        setChosenNavIndex(index);
        widgetSwiperObject?.slideTo(0); // eslint-disable-line
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        trackSelectedButtonClick(
            SHORTS_FILTERS_LINKS_TRACKING_DATA_ATTRS["data-feature-name"],
            SHORTS_FILTERS_LINKS_TRACKING_DATA_ATTRS["data-feature-instance"],
            undefined,
            label
        );
    };

    const navigationLinks: NavigationProps = {
        links: navigationData?.map(({ label }, index) => {
            const filterButton: NavButtonProps = {
                size: "24",
                label: label,
                type: ButtonTypes.TextButton,
                shape: ButtonShapes.TextButtonRect,
                variant: index === chosenNavIndex ? "filled" : "outlined",
                ariaPressed: index === chosenNavIndex,
                onClick: () => {
                    handleNavigationClick(index, label);
                },
            };
            return filterButton;
        }),
        orientation: "horizontalNav",
    };

    useEffect(() => {
        async function forYouABTestFetch() {
            try {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const response: TransformedCardData[] =
                    await personalizationABTest(variant);
                setShortsData(response);
            } catch (error) {
                setShortsData([]);
            }
        }

        if (!navigationData) return;
        const chosenNavItem: ShortsNavConstantProps =
            navigationData[chosenNavIndex];
        if (!chosenNavItem || !chosenNavItem.fetchFn) return;

        /** WEBX-3068-A/B Test For For You Filter on Vertical Video */
        if (chosenNavItem.label === "For You" && variant) {
            forYouABTestFetch().catch((error) => {
                setShortsData([]);
            });
        }

        setShortsData([]);
        chosenNavItem
            .fetchFn()
            .then((response: TransformedCardData[]) => {
                setShortsData(response);
            })
            .catch(() => {
                setShortsData([]);
            });
    }, [chosenNavIndex, navigationData, variant]);

    const cards = shortsData.map((item: TransformedCardData, index: number) => {
        const cardProps = {
            variant: CardVariantsEnum.MEDIA,
            key: `shorts-widget-${index}`,
            item: {
                ...item,
                image: {
                    ...item.image,
                    url: item.image?.aspects?.ratio9x16?.url || "",
                    loading: "lazy",
                },
            },
            renderAsButton: true,
            onClick: handleModalOpen(index),
            aspectRatio: 0.5625,
            showIcon: true,
        };

        return <Card {...cardProps} />;
    });

    const cardsWithAds = insertAdsAfterN(cards, 5);

    return (
        <PlaylistCore
            title={title}
            navigationLinks={navigationLinks}
            cardData={shortsData}
            playingVideoIndex={playingVideoIndex}
            modalIsOpen={modalIsOpen}
            onWidgetSwiper={onWidgetSwiper}
            onSlideChange={onSlideChange}
            handleClose={handleClose}
            aspectRatio={VIDEO_ASPECT_RATIOS["9x16"]}
            featureAttributionName="vertical-video"
            shouldDisableAds={true}
            autoplayNextOnVideoEnd={true}
        >
            {cardsWithAds.length > 0 ? cardsWithAds : PLACEHOLDER_CARDS}
        </PlaylistCore>
    );
};

export default ShortsWidget;
